import { useShallow } from 'zustand/react/shallow'

import { useAuthStore } from '@/stores/auth.store'

/**
 * Convenience hooks over the auth store.
 *
 * Why `useShallow`
 * ----------------
 * Zustand v5 sits on top of React's `useSyncExternalStore`, which
 * compares snapshots with `Object.is` (reference equality). A
 * naive selector like
 *
 *     useAuthStore((s) => ({ user: s.user, accessToken: s.accessToken }))
 *
 * allocates a brand-new object on every render. The reference
 * never matches the previous one, so `useSyncExternalStore`
 * concludes the snapshot has changed, schedules a re-render,
 * which calls the selector again, which produces yet another new
 * object… This is the canonical "Maximum update depth exceeded"
 * loop reported by React when running in `StrictMode`.
 *
 * `useShallow` wraps the selector so the *contents* of the
 * returned object are compared one level deep with `Object.is`.
 * As long as the underlying primitives don't change, the wrapper
 * returns the previous reference and React skips the re-render —
 * breaking the loop.
 *
 * Two slices, deliberately
 * ------------------------
 * - `useAuth` returns the read-only state primitives the UI
 *   reacts to.
 * - `useAuthActions` returns the action functions. Splitting them
 *   means a component that only needs `logout` doesn't re-render
 *   when `user` changes, and vice versa.
 */

export function useAuth() {
  return useAuthStore(
    useShallow((s) => ({
      user: s.user,
      accessToken: s.accessToken,
      isAuthenticated: s.user !== null && s.accessToken !== null,
      hydrated: s.hydrated,
    })),
  )
}

export function useAuthActions() {
  return useAuthStore(
    useShallow((s) => ({
      login: s.login,
      logout: s.logout,
      hydrate: s.hydrate,
      refresh: s.refresh,
    })),
  )
}
